import { useCallback, useEffect, useMemo, useState } from "react"

import { useEditor } from "@craftjs/core"
import { get, set } from "lodash"

import { ANIMATION_KEYS } from "../../properties/Animation/animationsPredefined"

export function useConfig({ key, cb, defaultCSS = "" }) {
  const { value: config } = useTailwindConfig(key)
  const [css, setCSS] = useState(cb(config))

  useEffect(() => {
    setCSS(cb(config))
  }, [config, cb])

  return useMemo(() => ({
    css: [defaultCSS, css].join('\n'),
    config,
    toJSON() {
      return this.css
    },
  }), [config, css, defaultCSS])
}

function useApplyCSSCallback() {
  return useCallback(config => Object.entries(config).map(([key, className]) => `
    ${key} {
      @apply ${className};
    }`).join('\n'), [])
}

export function useTailwindBase() {
  return useConfig({
    key: 'base',
    cb: useApplyCSSCallback(),
  })
}

export function useTailwindTheme() {
  return useConfig({
    key: 'theme',
    cb: useCallback(config => Object.entries(config).map(([key, value]) => `--${key}: ${value};`).join('\n'), []),
  })
}

export function useFont() {
  return useConfig({
    key: 'font',
    cb: useCallback(config => Object.values(config).filter(url => url).map(url => `@import url("${url}");\n`).join(''), []),
  })
}

export function useKeyframe() {
  const { value: customKeyframes } = useCustomKeyframe()
  const allKeyframes = useMemo(() => ({ ...ANIMATION_KEYS, ...customKeyframes }), [customKeyframes])

  return useConfig({
    key: 'keyframe',
    cb: useCallback(config => {
      const keyframes = config && !Array.isArray(config) ? Object.keys(config) : config
      return keyframes.length ? keyframes.map(name => allKeyframes[name] ? `@keyframes ${name} { ${allKeyframes[name]} }` : '').join('') : ''
    }, [allKeyframes]),
  })
}

export function useSharedPropsCSS() {
  const { value } = useRootCustom('stylePatterns')

  return Object
    .entries(value || {})
    .reduce((css, [suffix, props]) => {
      const {
        parsedClassName: className = '',
        opts: { prefix = 'pe-builder-' } = {},
      } = props || {}

      if (!className) {
        return css
      }

      return `${css}
    .${prefix || ''}${suffix} {
      @apply ${className};
      }`
    }, '')
}

export function useTailwindUtilities() {
  const sharedPropsCSS = useSharedPropsCSS()
  const applyCSSCallback = useApplyCSSCallback()

  return useConfig({
    key: 'utilities',
    cb: useCallback(config => `${applyCSSCallback(config)}`, [applyCSSCallback]),
    defaultCSS: sharedPropsCSS,
  })
}

export function useGlobalTailwindConfig() {
  const utilities = useTailwindUtilities()
  const theme = useTailwindTheme()
  const base = useTailwindBase()
  const tailwindCssConfig = useTailwindCssConfig()
  const font = useFont()
  const keyframe = useKeyframe()

  return useMemo(() => ({
    theme,
    tailwindCssConfig,
    font,
    keyframe,
    layers: {
      base,
      utilities,
    },
  }), [utilities, theme, base, tailwindCssConfig, font, keyframe])
}

function useRootCustom(prop, defaultValue) {
  const { store: { actions: { history: { throttle, ignore } } }, hasRoot, custom } = useEditor(({ nodes }) => ({
    hasRoot: !!nodes['ROOT'],
    custom: nodes['ROOT']?.data?.custom,
  }))

  const value = useMemo(() => get(custom, prop, {}), [custom, prop])
  const setCustom = useMemo(() => throttle(500).setCustom, [throttle]);
  const setIgnore = useMemo(() => ignore().setCustom, [ignore]);

  const setRootCustom = useCallback(value => {
    setCustom('ROOT', custom => {
      set(custom, prop, value)
    })
  }, [setCustom, prop])

  const setValue = useCallback(value => {
    if (!hasRoot) return

    setRootCustom(value)
  }, [setRootCustom, hasRoot])

  const setValueIgnoring = useCallback(value => {
    if (!hasRoot) return

    setIgnore('ROOT', custom => {
      set(custom, prop, value)
    })
  }, [setIgnore, prop, hasRoot])

  const updateValue = useCallback(cb => {
    if (!hasRoot) return

    setCustom('ROOT', custom => {
      const current = get(custom, prop) || {}
      set(custom, prop, cb(current))
    })
  }, [setCustom, prop, hasRoot])

  useEffect(() => {
    if (defaultValue !== undefined) {
      setValue(defaultValue)
    }
  }, [defaultValue, setValue])

  return {
    value,
    setValue,
    setValueIgnoring,
    updateValue,
  }
}

export function useImages() {
  return useRootCustom('images')
}

export function useStylePatterns(type, defaultValue) {
  const key = type ? `stylePatterns.${type}` : 'stylePatterns'
  return useRootCustom(key, defaultValue)
}

export function useSharedProp(type, defaultValue) {
  return useRootCustom(`sharedProps.${type}`, defaultValue)
}

export function useTailwindConfig(type, defaultValue) {
  return useRootCustom(`tailwind.${type}`, defaultValue)
}

export function useTailwindCssConfig() {
  return useRootCustom(`tailwind.tailwindCssConfig`)
}

export function useFontConfig() {
  return useRootCustom('tailwind.font')
}

export function useColors() {
  const { value, setValue } = useRootCustom('colors')
  return { value: value.length ? value : [], setValue }
}

export function useUsedColors() {
  const { value, setValue } = useRootCustom('usedColors')
  return { value: value.length ? value : [], setValue }
}

export function useCustomKeyframe() {
  const { value, setValue } = useRootCustom('keyframe')

  const parsedValue = value && value.length ? Object.fromEntries(value.map(({ name, keyframe }) => [name, keyframe])) : value
  return { value: parsedValue, setValue }
}

export function useCustomUtilities() {
  const { value, setValue } = useRootCustom('utilities')
  return { value: value.length ? value : [], setValue }
}

export function useKeyframeConfig() {
  const { value, setValue } = useRootCustom('tailwind.keyframe')

  const parsedValue = value && !Array.isArray(value) ? Object.keys(value) : value
  return { value: parsedValue, setValue }
}

export function useDefaultTailwindConfig() {
  useTailwindConfig('base', DEFAULT_BASE)
}

export function useMetadata(type, defaultValue) {
  return useRootCustom(`metadata${type ? `.${type}` : ''}`, defaultValue)
}

export function useModuleMetadata(type, defaultValue) {
  return useMetadata(`modules.${type}`, defaultValue)
}

const DEFAULT_BASE = {
  h1: 'text-6xl',
  h2: 'text-5xl',
  h3: 'text-4xl',
  h4: 'text-3xl',
  h5: 'text-2xl',
  h6: 'text-xl',
  p: 'text-base',
  text: 'text-base',
  small: 'text-sm',
  big: 'text-lg',
}
